import {
  Command,
  Editor,
  DowncastWriter,
  Element,
  ViewElement,
  ImageUtils,
  inlineHighlight,
  TwoStepCaretMovement,
  ButtonView,
  toWidget,
  WidgetResize,
} from "ckeditor5";
import { css } from "styled-components";

import { IconFolderPlusLine } from "@examspark/common-ui/dist/components/icons";

import { upcastUnwrapElement } from "../util/converters";

import { PluginWithStyles } from "./PluginWithStyles";

export const UI_BUTTON_INSERT_SHARED_RESOURCE = "insertSharedResource";
export const COMMAND_INSERT_SHARED_RESOURCE = "insertSharedResource";

type ResourceLinkAttributeValue = {
  href: string;
  resourceKey: string;
  resourceType: string;
};

export type ResourceDetails = {
  id?: string;
  key?: string;
  type?: string;
};

export type SharedResource = { details?: ResourceDetails } & (
  | { type: "image"; src: string; width: string }
  | { type: "link"; href: string; text: string }
  | { type: "audio" | "video"; src: string; mimeType: string }
);

export type SharedResourceApi = {
  //
  // @todo: we could have this callback take a SelectedResource parameter
  // for editing existing resources
  //
  onBrowseForResource: () => Promise<SharedResource | null>;
};

export class InsertResourceCommand extends Command {
  private api: SharedResourceApi;

  constructor(editor: Editor, api: SharedResourceApi) {
    super(editor);
    this.api = api;
  }

  async execute() {
    const result = await this.api.onBrowseForResource();
    if (result == null) {
      return null;
    }

    switch (result.type) {
      case "image":
        this.editor.plugins.get(ImageUtils).insertImage({
          src: result.src,
          width: result.width,
          resourceId: result.details?.id,
        });
        break;
      case "link":
        this.editor.model.change((writer) => {
          this.editor.model.insertContent(
            writer.createText(result.text, {
              resourceLink: {
                href: result.href,
                resourceKey: result.details?.key,
                resourceType: result.details?.type,
              },
            }),
          );
        });
        break;
      case "audio":
      case "video":
        this.editor.model.change((writer) => {
          const element = writer.createElement("resourceMedia", {
            type: result.type,
            mimeType: result.mimeType,
            src: result.src,
          });
          this.editor.model.insertContent(element);
        });
        break;
      default:
        console.log(result);
        break;
    }
  }
}

export default class SharedResourceInsert extends PluginWithStyles {
  private api: SharedResourceApi | undefined;

  static buildEditorStyles() {
    return css`
      figure.examspark-media {
        margin: 0.9rem 0;
      }
    `;
  }

  constructor(editor: Editor) {
    super(editor);

    this.api = editor.config.get("sharedResourceApi");
  }

  init() {
    this.editor.model.schema.register("$resource", {
      allowAttributes: ["resourceId", "resourceKey", "resourceType"],
    });

    this.editor.conversion.attributeToAttribute({
      model: "resourceId",
      view: "data-examspark-id",
    });

    this.editor.conversion.attributeToAttribute({
      model: "resourceKey",
      view: "data-examspark-key",
    });

    this.editor.conversion.attributeToAttribute({
      model: "resourceType",
      view: "data-examspark-type",
    });

    this.registerImages();
    this.registerLinks();
    this.registerMedia();

    if (this.api == null) {
      return;
    }

    const insertCommand = new InsertResourceCommand(this.editor, this.api);
    this.editor.commands.add(COMMAND_INSERT_SHARED_RESOURCE, insertCommand);

    this.editor.ui.componentFactory.add(
      UI_BUTTON_INSERT_SHARED_RESOURCE,
      (locale) => {
        const button = new ButtonView(locale);

        button.set({
          icon: IconFolderPlusLine.Content,
          tooltip: this.editor.t("Insert Shared Resource"),
        });

        button.on("execute", () => insertCommand.execute());
        button.bind("isEnabled").to(insertCommand, "isEnabled");

        return button;
      },
    );
  }

  private registerImages() {
    this.editor.model.schema.extend("imageBlock", {
      allowAttributesOf: "$resource",
    });

    this.editor.model.schema.extend("imageInline", {
      allowAttributesOf: "$resource",
    });
  }

  private registerLinks() {
    this.editor.model.schema.extend("$text", {
      allowAttributes: "resourceLink",
    });

    this.editor.conversion.for("downcast").attributeToElement({
      model: "resourceLink",
      view: (value: ResourceLinkAttributeValue | undefined, { writer }) => {
        return writer.createAttributeElement("a", {
          "data-examspark-key": value?.resourceKey,
          "data-examspark-type": value?.resourceType,
          href: value?.href,
        });
      },
    });

    this.editor.conversion.for("upcast").elementToAttribute({
      view: (e) => {
        if (e.name !== "a") return null;

        if (
          !e.hasAttribute("data-examspark-key") ||
          !e.hasAttribute("data-examspark-type") ||
          !e.hasAttribute("href")
        ) {
          return null;
        }

        return {
          name: true,
          attributes: ["data-examspark-key", "data-examspark-type", "href"],
        };
      },
      model: {
        key: "resourceLink",
        value: (e: ViewElement): ResourceLinkAttributeValue => ({
          //
          // we know these can't be null we wouldn't have matched if they were
          //
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          href: e.getAttribute("href")!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          resourceKey: e.getAttribute("data-examspark-key")!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          resourceType: e.getAttribute("data-examspark-type")!,
        }),
      },
      converterPriority: "high",
    });

    this.editor.plugins
      .get(TwoStepCaretMovement)
      .registerAttribute("resourceLink");
    inlineHighlight(this.editor, "resourceLink", "a", "ck-link_selected");
  }

  private registerMedia() {
    this.editor.model.schema.register("resourceMedia", {
      allowWhere: ["$block", "$blockObject", "$container"],
      allowAttributes: ["src", "mimeType", "type", "width"],
      allowAttributesOf: "$resource",
      isContent: true,
    });

    //
    // Workaround to prevent DataFilter from setting up <audio> and <video>
    // as "raw content" elements (which interferes with our upcast)
    //
    // - https://github.com/ckeditor/ckeditor5/blob/v40.1.0/packages/ckeditor5-html-support/src/datafilter.ts#L389
    // - https://github.com/ckeditor/ckeditor5/blob/v40.1.0/packages/ckeditor5-html-support/src/datafilter.ts#L592
    //
    this.editor.model.schema.register("htmlAudio", {});
    this.editor.model.schema.register("htmlVideo", {});

    this.editor.conversion
      .for("upcast")
      .add(
        upcastUnwrapElement(
          { name: "figure", classes: ["examspark-media"] },
          { name: /video|audio/ },
        ),
      );

    this.editor.conversion.for("upcast").elementToElement({
      view: {
        name: /audio|video/,
      },
      model: (e, { writer, consumable }) => {
        const source = Array.from(e.getChildren()).find(
          (e) => e.is("element") && e.name === "source",
        ) as ViewElement | undefined;
        if (source == null) return null;

        consumable.consume(e, {
          name: true,
          attributes: ["controls", "controllist"],
        });

        consumable.consume(source, {
          name: true,
          attributes: ["src", "type"],
        });

        const media = writer.createElement("resourceMedia", {
          type: e.name,
          mimeType: source.getAttribute("type"),
          src: source.getAttribute("src"),
        });

        const figure = e.parent;

        if (
          figure?.is("element") &&
          figure.name === "figure" &&
          figure.hasStyle("width")
        ) {
          writer.setAttribute("width", figure.getStyle("width"), media);
        } else if (e.hasAttribute("width")) {
          writer.setAttribute("width", e.getAttribute("width"), media);
        }

        return media;
      },
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "resourceMedia",
      view: (e, { writer }) => this.createMediaViewElement(e, writer),
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "resourceMedia",
      view: (e, { writer }) => {
        const widget = toWidget(
          this.createMediaViewElement(e, writer),
          writer,
          {
            label: "media",
            hasSelectionHandle: true,
          },
        );

        if (e.getAttribute("type") === "video") {
          this.editor.plugins.get(WidgetResize).attachTo({
            unit: "px",
            modelElement: e,
            viewElement: widget,
            editor: this.editor,
            getHandleHost: (e) => e,
            getResizeHost: (e) => e,
            onCommit: (newValue) => {
              this.editor.model.change((writer) =>
                writer.setAttribute("width", newValue, e),
              );
              this.editor.editing.view.change((writer) =>
                writer.setStyle("width", newValue, widget),
              );
            },
            isCentered: () => false,
          });
        }

        return widget;
      },
    });
  }

  private createMediaViewElement(e: Element, writer: DowncastWriter) {
    const type = e.getAttribute("type") as "video" | "audio";

    const media = writer.createContainerElement(type, {
      controls: "",
      controllist: "nodownload",
    });

    writer.insert(writer.createPositionAt(media, 0), [
      writer.createEmptyElement("source", {
        src: e.getAttribute("src"),
        type: e.getAttribute("mimeType"),
      }),
      writer.createText(`Your browser does not support the ${type} element.`),
    ]);

    const figure = writer.createContainerElement("figure", {
      class: "examspark-media",
    });

    writer.insert(writer.createPositionAt(figure, 0), media);

    if (type === "video") {
      writer.setAttribute("width", "100%", media);
      if (e.hasAttribute("width")) {
        writer.setStyle("width", e.getAttribute("width") as string, figure);
      }
    }

    return figure;
  }
}

declare module "ckeditor5" {
  interface EditorConfig {
    sharedResourceApi?: SharedResourceApi;
  }
}
